/**
 * @module @liquidefy/code-editor/themes/color/oklch
 * @version 1.0.0
 * @license LQD Research LLC
 *
 * OKLCH / OKLAB perceptual color space engine.
 * Pure math — no side effects, no DOM.
 *
 * Implements the Björn Ottosson OKLAB/OKLCH color model with
 * mathematically correct sRGB ↔ Linear RGB ↔ OKLAB ↔ OKLCH conversions.
 *
 * @rust oklch.rs — false; 4/2/26; sm
 */

// ═══ §1 TYPES ═══

/** sRGB color, channels 0–1 */
export type SRGB = { r: number; g: number; b: number };

/** Linear-light RGB, channels 0–1 */
export type LinearRGB = { r: number; g: number; b: number };

/** OKLAB perceptual color: L 0–1 (lightness), a/b chroma axes */
export type OKLAB = { L: number; a: number; b: number };

/** OKLCH polar form: L 0–1, C 0–~0.4 (chroma), H 0–360 (hue degrees) */
export type OKLCH = { L: number; C: number; H: number };

/** CSS hex string like '#60a5fa' */
export type HexColor = string;

// ═══ §2 INTERNAL HELPERS ═══

/** Clamp a number to [min, max] */
function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/** Cube root that preserves sign */
function cbrt(x: number): number {
  return x >= 0 ? Math.pow(x, 1 / 3) : -Math.pow(-x, 1 / 3);
}

/** Normalize a hue angle to [0, 360) */
function normalizeHue(h: number): number {
  h = h % 360;
  return h < 0 ? h + 360 : h;
}

/** Convert a single 0–1 channel to 2-digit hex */
function channelToHex(c: number): string {
  const byte = Math.round(clamp(c, 0, 1) * 255);
  return byte.toString(16).padStart(2, '0');
}

// ═══ §3 HEX ↔ sRGB ═══

/**
 * Parse a CSS hex color ('#rgb', '#rrggbb', '#rrggbbaa') to sRGB 0–1.
 * Alpha channel is silently discarded.
 */
export function hexToSRGB(hex: HexColor): SRGB {
  let h = hex.startsWith('#') ? hex.slice(1) : hex;

  // Expand shorthand (#rgb → #rrggbb)
  if (h.length === 3 || h.length === 4) {
    h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
  }

  const ri = parseInt(h.slice(0, 2), 16);
  const gi = parseInt(h.slice(2, 4), 16);
  const bi = parseInt(h.slice(4, 6), 16);

  return {
    r: ri / 255,
    g: gi / 255,
    b: bi / 255,
  };
}

/** Convert sRGB 0–1 to CSS hex '#rrggbb' */
export function srgbToHex(color: SRGB): HexColor {
  return '#' + channelToHex(color.r) + channelToHex(color.g) + channelToHex(color.b);
}

// ═══ §4 sRGB ↔ LINEAR RGB (GAMMA) ═══

/**
 * sRGB → Linear RGB (gamma decode).
 *
 * IEC 61966-2-1 transfer function:
 *   x <= 0.04045  → x / 12.92
 *   x >  0.04045  → ((x + 0.055) / 1.055) ^ 2.4
 */
export function srgbToLinear(color: SRGB): LinearRGB {
  function decode(c: number): number {
    const v = clamp(c, 0, 1);
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }
  return { r: decode(color.r), g: decode(color.g), b: decode(color.b) };
}

/**
 * Linear RGB → sRGB (gamma encode).
 *
 * Inverse IEC 61966-2-1:
 *   x <= 0.0031308  → 12.92 * x
 *   x >  0.0031308  → 1.055 * x^(1/2.4) - 0.055
 */
export function linearToSRGB(color: LinearRGB): SRGB {
  function encode(c: number): number {
    const v = clamp(c, 0, 1);
    return v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  }
  return { r: encode(color.r), g: encode(color.g), b: encode(color.b) };
}

// ═══ §5 LINEAR RGB ↔ OKLAB (BJÖRN OTTOSSON) ═══

/**
 * Linear RGB → OKLAB.
 *
 * Step 1: Linear RGB → LMS (cone response)
 *   l = 0.4122214708 r + 0.5363325363 g + 0.0514459929 b
 *   m = 0.2119034982 r + 0.6806995451 g + 0.1073969566 b
 *   s = 0.0883024619 r + 0.2220049174 g + 0.6396926208 b
 *
 * Step 2: Cube root (perceptual non-linearity)
 *   l_ = cbrt(l),  m_ = cbrt(m),  s_ = cbrt(s)
 *
 * Step 3: LMS' → OKLAB
 *   L = 0.2104542553 l_ + 0.7936177850 m_ - 0.0040720468 s_
 *   a = 1.9779984951 l_ - 2.4285922050 m_ + 0.4505937099 s_
 *   b = 0.0259040371 l_ + 0.7827717662 m_ - 0.8086757660 s_
 */
export function linearToOKLAB(color: LinearRGB): OKLAB {
  const { r, g, b } = color;

  // Linear RGB → LMS
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2220049174 * g + 0.6396926208 * b;

  // Cube root
  const l_ = cbrt(l);
  const m_ = cbrt(m);
  const s_ = cbrt(s);

  // LMS' → OKLAB
  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  };
}

/**
 * OKLAB → Linear RGB.
 *
 * Step 1: OKLAB → LMS' (inverse of Step 3)
 *   l_ = L + 0.3963377774 a + 0.2158037573 b
 *   m_ = L - 0.1055613458 a - 0.0638541728 b
 *   s_ = L - 0.0894841775 a - 1.2914855480 b
 *
 * Step 2: Cube (undo perceptual non-linearity)
 *   l = l_³,  m = m_³,  s = s_³
 *
 * Step 3: LMS → Linear RGB (inverse of Step 1)
 *   r =  4.0767416621 l - 3.3077115913 m + 0.2309699292 s
 *   g = -1.2684380046 l + 2.6097574011 m - 0.3413193965 s
 *   b = -0.0041960863 l - 0.7034186147 m + 1.7076147010 s
 */
export function oklabToLinear(color: OKLAB): LinearRGB {
  const { L, a, b } = color;

  // OKLAB → LMS'
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  // Cube
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  // LMS → Linear RGB
  return {
    r:  4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  };
}

// ═══ §6 OKLAB ↔ OKLCH (CARTESIAN ↔ POLAR) ═══

/**
 * OKLAB (Cartesian a, b) → OKLCH (polar C, H).
 *   C = sqrt(a² + b²)
 *   H = atan2(b, a) in degrees, normalized to [0, 360)
 */
export function oklabToOKLCH(color: OKLAB): OKLCH {
  const C = Math.sqrt(color.a * color.a + color.b * color.b);
  let H = (Math.atan2(color.b, color.a) * 180) / Math.PI;
  H = normalizeHue(H);

  // When chroma is effectively zero, hue is undefined — default to 0
  return { L: color.L, C, H: C < 1e-8 ? 0 : H };
}

/**
 * OKLCH (polar C, H) → OKLAB (Cartesian a, b).
 *   a = C * cos(H)
 *   b = C * sin(H)
 */
export function oklchToOKLAB(color: OKLCH): OKLAB {
  const hRad = (color.H * Math.PI) / 180;
  return {
    L: color.L,
    a: color.C * Math.cos(hRad),
    b: color.C * Math.sin(hRad),
  };
}

// ═══ §7 CONVENIENCE FULL-CHAIN CONVERSIONS ═══

/** Hex → sRGB → Linear → OKLAB → OKLCH */
export function hexToOKLCH(hex: HexColor): OKLCH {
  return oklabToOKLCH(linearToOKLAB(srgbToLinear(hexToSRGB(hex))));
}

/**
 * OKLCH → OKLAB → Linear → sRGB → Hex.
 * Clamps linear values to [0, 1] before gamma encoding to stay in sRGB gamut.
 */
export function oklchToHex(color: OKLCH): HexColor {
  const lab = oklchToOKLAB(color);
  const linear = oklabToLinear(lab);

  // Clamp to sRGB gamut
  const clamped: LinearRGB = {
    r: clamp(linear.r, 0, 1),
    g: clamp(linear.g, 0, 1),
    b: clamp(linear.b, 0, 1),
  };

  return srgbToHex(linearToSRGB(clamped));
}

/** OKLCH → OKLAB → Linear RGB (unclamped, may be out of sRGB gamut) */
export function oklchToLinearRGB(color: OKLCH): LinearRGB {
  return oklabToLinear(oklchToOKLAB(color));
}

/** OKLCH → sRGB (clamped to 0–1) */
export function oklchToSRGB(color: OKLCH): SRGB {
  const linear = oklchToLinearRGB(color);
  return linearToSRGB({
    r: clamp(linear.r, 0, 1),
    g: clamp(linear.g, 0, 1),
    b: clamp(linear.b, 0, 1),
  });
}

/** sRGB → OKLCH */
export function srgbToOKLCH(color: SRGB): OKLCH {
  return oklabToOKLCH(linearToOKLAB(srgbToLinear(color)));
}

// ═══ §8 PERCEPTUAL LIGHTNESS OPERATIONS ═══

/**
 * Increase lightness by `amount`.
 * Result L is clamped to [0, 1].
 */
export function lighten(color: OKLCH, amount: number): OKLCH {
  return { ...color, L: clamp(color.L + amount, 0, 1) };
}

/**
 * Decrease lightness by `amount`.
 * Result L is clamped to [0, 1].
 */
export function darken(color: OKLCH, amount: number): OKLCH {
  return { ...color, L: clamp(color.L - amount, 0, 1) };
}

// ═══ §9 PERCEPTUAL CHROMA OPERATIONS ═══

/**
 * Increase chroma by `amount`.
 * Result C is clamped to [0, 0.5] (practical OKLCH max).
 */
export function saturate(color: OKLCH, amount: number): OKLCH {
  return { ...color, C: clamp(color.C + amount, 0, 0.5) };
}

/**
 * Decrease chroma by `amount`.
 * Result C is clamped to [0, 0.5].
 */
export function desaturate(color: OKLCH, amount: number): OKLCH {
  return { ...color, C: clamp(color.C - amount, 0, 0.5) };
}

// ═══ §10 HUE OPERATIONS ═══

/** Rotate hue by `degrees`. Result is normalized to [0, 360). */
export function rotateHue(color: OKLCH, degrees: number): OKLCH {
  return { ...color, H: normalizeHue(color.H + degrees) };
}

// ═══ §11 SETTERS ═══

/** Set exact lightness, clamped to [0, 1]. */
export function setLightness(color: OKLCH, L: number): OKLCH {
  return { ...color, L: clamp(L, 0, 1) };
}

/** Set exact chroma, clamped to [0, 0.5]. */
export function setChroma(color: OKLCH, C: number): OKLCH {
  return { ...color, C: clamp(C, 0, 0.5) };
}

/** Set exact hue, normalized to [0, 360). */
export function setHue(color: OKLCH, H: number): OKLCH {
  return { ...color, H: normalizeHue(H) };
}

// ═══ §12 PERCEPTUAL INTERPOLATION ═══

/**
 * Perceptually interpolate between two OKLCH colors.
 *
 * L and C are linearly interpolated. H uses shortest-arc interpolation
 * to avoid going the long way around the hue circle.
 *
 * @param a - Start color
 * @param b - End color
 * @param t - Interpolation factor, 0 = a, 1 = b
 */
export function mix(a: OKLCH, b: OKLCH, t: number): OKLCH {
  const tc = clamp(t, 0, 1);

  const L = a.L + (b.L - a.L) * tc;
  const C = a.C + (b.C - a.C) * tc;

  // Shortest-arc hue interpolation
  let dH = b.H - a.H;
  if (dH > 180) dH -= 360;
  if (dH < -180) dH += 360;

  // If either color is achromatic (C ≈ 0), use the other's hue
  let H: number;
  if (a.C < 1e-8 && b.C < 1e-8) {
    H = 0;
  } else if (a.C < 1e-8) {
    H = b.H;
  } else if (b.C < 1e-8) {
    H = a.H;
  } else {
    H = normalizeHue(a.H + dH * tc);
  }

  return { L, C, H };
}

// ═══ §13 PERCEPTUAL DISTANCE ═══

/**
 * Perceptual distance between two colors (deltaE in OKLAB space).
 *
 * Computed as Euclidean distance in OKLAB:
 *   deltaE = sqrt((L1-L2)² + (a1-a2)² + (b1-b2)²)
 *
 * This correlates well with human perception — unlike deltaE in sRGB.
 */
export function distance(a: OKLCH, b: OKLCH): number {
  const labA = oklchToOKLAB(a);
  const labB = oklchToOKLAB(b);

  const dL = labA.L - labB.L;
  const da = labA.a - labB.a;
  const db = labA.b - labB.b;

  return Math.sqrt(dL * dL + da * da + db * db);
}

// ═══ §14 LUMINANCE UTILITY ═══

/**
 * Compute relative luminance (Y) from an OKLCH color.
 * Uses the CIE formula: Y = 0.2126 R + 0.7152 G + 0.0722 B (linear).
 */
export function luminance(color: OKLCH): number {
  const lin = oklchToLinearRGB(color);
  const r = clamp(lin.r, 0, 1);
  const g = clamp(lin.g, 0, 1);
  const b = clamp(lin.b, 0, 1);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
